/**
 * Alert Component
 *
 * Local implementation for equa-web. The equa-patternlib does not
 * currently export an Alert component, so this provides a themed
 * alert/notification box supporting variant, title, dismissible, and children.
 */

import React, { useState, ReactNode } from 'react'
import styled, { css } from 'styled-components'

// ── Types ──

export interface AlertProps {
    /** Visual style variant */
    variant?: 'info' | 'success' | 'warning' | 'error'
    /** Bold heading rendered above children */
    title?: string
    /** Whether a dismiss (×) button is shown. Defaults to true. */
    dismissible?: boolean
    /** Called when the user dismisses the alert */
    onDismiss?: () => void
    children?: ReactNode
    className?: string
}

export interface AlertTitleProps {
    children?: ReactNode
    className?: string
}

export interface AlertDescriptionProps {
    children?: ReactNode
    className?: string
}

// ── Colour helpers ──

const VARIANT_COLOURS = {
    info: { bg: '#e8f4fd', border: '#90caf9', text: '#0d47a1', icon: 'ℹ️' },
    success: { bg: '#e8f5e9', border: '#81c784', text: '#1b5e20', icon: '✅' },
    warning: { bg: '#fff8e1', border: '#ffca28', text: '#e65100', icon: '⚠️' },
    error: { bg: '#fce4ec', border: '#ef5350', text: '#b71c1c', icon: '❌' },
} as const

// ── Styled primitives ──

const variantStyle = (variant: AlertProps['variant'] = 'info') => {
    const c = VARIANT_COLOURS[variant]
    return css`
    background: ${c.bg};
    border: 1px solid ${c.border};
    color: ${c.text};
  `
}

const Wrapper = styled.div<{ $variant: AlertProps['variant'] }>`
  ${({ $variant }) => variantStyle($variant)}
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  line-height: 1.5;
  position: relative;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 6px;
`

const DismissButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  opacity: 0.6;
  line-height: 1;
  padding: 0;
  color: inherit;

  &:hover {
    opacity: 1;
  }
`

// ── Components ──

export const AlertTitle: React.FC<AlertTitleProps> = ({ children, className }) => (
    <TitleRow className={className}>{children}</TitleRow>
)

export const AlertDescription: React.FC<AlertDescriptionProps> = ({ children, className }) => (
    <div className={className}>{children}</div>
)

export const Alert: React.FC<AlertProps> = ({
    variant = 'info',
    title,
    dismissible = true,
    onDismiss,
    children,
    className,
}) => {
    const [visible, setVisible] = useState(true)

    if (!visible) return null

    const handleDismiss = () => {
        setVisible(false)
        onDismiss?.()
    }

    const colours = VARIANT_COLOURS[variant]

    return (
        <Wrapper $variant={variant} className={className}>
            {dismissible && (
                <DismissButton onClick={handleDismiss} aria-label="Dismiss">
                    ×
                </DismissButton>
            )}
            {title && (
                <TitleRow>
                    <span>{colours.icon}</span>
                    <span>{title}</span>
                </TitleRow>
            )}
            {children}
        </Wrapper>
    )
}

export default Alert
